import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { companyInfo } from '../../mock';
import Footer from '../../components/Footer';

const CancellationPolicy = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-dark)' }}>
      {/* Navigation */}
      <nav className="border-b" style={{ borderColor: 'rgba(9, 166, 163, 0.2)', backgroundColor: 'rgba(36, 37, 38, 0.95)' }}>
        <div className="container mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-3">
            <img src={companyInfo.logo} alt={companyInfo.name} className="h-16 w-auto" />
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="container mx-auto px-6 py-12 max-w-4xl">
        <Link to="/" className="inline-flex items-center gap-2 text-accent-teal hover:text-white transition-colors duration-300 mb-8">
          <ArrowLeft size={20} />
          <span>Back to Home</span>
        </Link>

        <h1 className="text-4xl md:text-5xl font-bold text-white mb-8">Cancellation & Refund Policy</h1>
        
        <div className="text-neutral-silver leading-relaxed space-y-6">
          <p className="text-lg">Last updated: December 2024</p>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">Subscription Cancellation</h2>
            <p className="mb-4">
              At NT Studios, we want you to be completely satisfied with our AI tools. You can cancel your subscription at any time through your account settings or by contacting our support team.
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>You can cancel your subscription at any time</li>
              <li>Cancellation takes effect at the end of your current billing cycle</li>
              <li>You will continue to have access to premium features until the end of your paid period</li>
              <li>No further charges will be made after cancellation</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">How to Cancel</h2>
            <ol className="list-decimal list-inside space-y-2 ml-4">
              <li>Log in to your NT Studios account</li>
              <li>Navigate to Account Settings</li>
              <li>Click on "Subscription" tab</li>
              <li>Select "Cancel Subscription"</li>
              <li>Confirm your cancellation</li>
            </ol>
            <p className="mt-4">
              Alternatively, you can contact us at <a href="mailto:support@ntstudios.com" className="text-accent-teal hover:underline">support@ntstudios.com</a> to request cancellation.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">Refund Policy</h2>
            <p className="mb-4">
              We offer a <strong className="text-accent-teal">7-day money-back guarantee</strong> on all new subscriptions.
            </p>
            <h3 className="text-xl font-semibold text-white mb-3 mt-6">Eligible for Refund:</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Refund requests made within 7 days of initial purchase</li>
              <li>First-time subscribers only</li>
              <li>Technical issues preventing use of the service (if unresolved)</li>
            </ul>

            <h3 className="text-xl font-semibold text-white mb-3 mt-6">Not Eligible for Refund:</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Refund requests made after 7 days of purchase</li>
              <li>Subscription renewals (monthly or annual)</li>
              <li>Violations of our Terms of Service</li>
              <li>Unused portions of a subscription after the 7-day period</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">Payment Processing</h2>
            <p className="mb-4">
              All payments are processed securely through <strong className="text-accent-teal">Razorpay</strong>. Refunds, when approved, will be processed back to your original payment method within 5-7 business days.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">How to Request a Refund</h2>
            <p className="mb-4">
              To request a refund within the 7-day guarantee period:
            </p>
            <ol className="list-decimal list-inside space-y-2 ml-4">
              <li>Email us at <a href="mailto:support@ntstudios.com" className="text-accent-teal hover:underline">support@ntstudios.com</a></li>
              <li>Include your account email and order/transaction ID</li>
              <li>Briefly explain the reason for your refund request</li>
              <li>We will process your request within 2-3 business days</li>
            </ol>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">Downgrading Plans</h2>
            <p className="mb-4">
              You can downgrade your subscription plan at any time. The change will take effect at the start of your next billing cycle. No refunds or prorated credits are provided for downgrades.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">Free Trial Cancellation</h2>
            <p className="mb-4">
              If you're on a free trial, you can cancel at any time before the trial ends to avoid being charged. There are no cancellation fees.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 mt-8">Contact Us</h2>
            <p>
              If you have any questions about our Cancellation & Refund Policy, please contact us:
            </p>
            <ul className="list-none space-y-2 mt-4">
              <li>Email: <a href="mailto:support@ntstudios.com" className="text-accent-teal hover:underline">support@ntstudios.com</a></li>
              <li>Via our <Link to="/#contact" className="text-accent-teal hover:underline">contact form</Link></li>
            </ul>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CancellationPolicy;
